import { partition } from "es-toolkit";

import {
    LINE_TYPES,
    LINE_TAGS_ACCESS,
    LINE_GROUP_TAGS_ACCESS,
    PLANT_TAGS_ACCESS,
} from "@/lib/ManufacturingTags.generated";
import { getLineType, getLineGroupForUser, getPlantTags } from "@/lib/businessLogic";

import type {
    IShiftPersonLineRec,
    IShiftPersonLineStoreRec,
    IShiftPersonLineStoreRecItem,
    IShiftRequiredPersonCountItem,
    ITags,
} from "@/lib/backend/manufacturing2.generated.types";

/**
 * Matches {@link postProcessLines}: missing `type` on tags / empty `getLineType` cache value is treated as ordinary
 * for pool scheduling partition, so UUID splits agree with line splits after post-processing.
 */
function lineTypeForPoolSchedulingPartition(rawType: string): string {
    return rawType === "" ? LINE_TYPES.ordinary : rawType;
}

/**
 * Gets list of line types of lines that are used as pool lines in pool mode.
 */
export function getPoolModePoolLineTypes(): string[] {
    return [LINE_TYPES.pool];
}

/**
 * Gets list of line types of lines that are used for scheduling in pool mode.
 */
export function getPoolModeSchedulingLineTypes(advancedPoolSchedulingEnabled = false): string[] {
    if (advancedPoolSchedulingEnabled) {
        return [LINE_TYPES.ordinary, LINE_TYPES.assembly, LINE_TYPES.special_activities];
    }

    return [LINE_TYPES.ordinary];
}

/**
 * Plant-level capability: shift board may use advanced pool scheduling when combined with a pool line group.
 * Does not imply the current line group uses pool mode — use {@link lineGroupUsesAdvancedPoolScheduling} for that.
 */
export function isAdvancedPoolSchedulingEnabled(plantUuid: string): boolean {
    const plantTags = getPlantTags(plantUuid);
    return PLANT_TAGS_ACCESS.shift_board_advanced_pool_scheduling(plantTags);
}

/**
 * Checks if this line group is in pool mode.
 */
export function lineGroupUsesPool(lineGroupUuid: string): boolean {
    const lineGroup = getLineGroupForUser(lineGroupUuid);
    return LINE_GROUP_TAGS_ACCESS.has_pool(lineGroup?.tags);
}

/**
 * Checks if this line group is in pool mode and the plant allows advanced pool scheduling (split table, week-pool UI, etc.).
 */
export function lineGroupUsesAdvancedPoolScheduling(plantUuid: string, lineGroupUuid: string): boolean {
    return lineGroupUsesPool(lineGroupUuid) && isAdvancedPoolSchedulingEnabled(plantUuid);
}

/**
 * Checks if line is used as pool line in pool mode.
 */
export function isPoolModePoolLine(lineTags: ITags): boolean {
    const type = LINE_TAGS_ACCESS.type(lineTags);
    return getPoolModePoolLineTypes().includes(type);
}

/**
 * Checks if line is used as scheduling line in pool mode.
 */
export function isPoolModeSchedulingLine(lineTags: ITags, plantUuid: string): boolean {
    const type = lineTypeForPoolSchedulingPartition(LINE_TAGS_ACCESS.type(lineTags));
    const advancedPoolSchedulingEnabled = isAdvancedPoolSchedulingEnabled(plantUuid);
    return getPoolModeSchedulingLineTypes(advancedPoolSchedulingEnabled).includes(type);
}

/**
 * Partitions line UUIDs by pool scheduling line type.
 * @returns `[pooledLineUuids, nonPooledLineUuids]` — predicate-true first, same tuple order as `partition` from es-toolkit.
 */
export function partitionLineUuidsByPoolSchedulingRole(
    lineUuids: string[],
    plantUuid: string
): [pooledLineUuids: string[], nonPooledLineUuids: string[]] {
    const pooledLineTypes = new Set(getPoolModeSchedulingLineTypes(isAdvancedPoolSchedulingEnabled(plantUuid)));
    return partition(lineUuids, uuid => {
        const typeForPartition = lineTypeForPoolSchedulingPartition(getLineType(uuid));
        return pooledLineTypes.has(typeForPartition);
    });
}

/**
 * First shift-board line whose tags mark it as pool line (`LINE_TYPES.pool`).
 * When using pool based shift-board, one pool line per line-group is expected.
 */
export function findPoolLine(lines: IShiftPersonLineRec[]): IShiftPersonLineRec | null {
    return lines.find(line => isPoolModePoolLine(line.line_tags)) ?? null;
}

/**
 * Partitions shift-board lines into scheduling (pooled) vs non-scheduling buckets.
 * @returns `[pooledLines, nonPooledLines]` — predicate-true first, same tuple order as `partition` from es-toolkit.
 * When the line group is not in pool mode, returns `[[], lines]` (no scheduling split).
 */
export function partitionLinesByPoolSchedulingRole(
    lines: IShiftPersonLineRec[],
    plantUuid: string,
    lineGroupUuid: string
): [pooledLines: IShiftPersonLineRec[], nonPooledLines: IShiftPersonLineRec[]] {
    if (!lineGroupUsesPool(lineGroupUuid)) {
        return [[], lines];
    }

    return partition(lines, line => isPoolModeSchedulingLine(line.line_tags, plantUuid));
}

/**
 * Sorts given shift-board lines by line weight and adds line type tag if not present.
 */
export function postProcessLines(lines: IShiftPersonLineRec[]): IShiftPersonLineRec[] {
    const sortedLines = [...lines].sort((a, b) => a.weight - b.weight);
    return sortedLines.map(line => ({
        ...line,
        line_tags: {
            ...line.line_tags,
            type: lineTypeForPoolSchedulingPartition(LINE_TAGS_ACCESS.type(line.line_tags)),
        },
    }));
}

/**
 * Sums list of required worker hours per shift (by line) into worker-hours per shift totals.
 */
export function sumWorkerHoursByShift(workerHoursPerShiftItems: IShiftRequiredPersonCountItem[]): Map<string, number> {
    const hoursByShiftTag: Map<string, number> = new Map();

    for (const item of workerHoursPerShiftItems) {
        const hours = hoursByShiftTag.get(item.shift_tag) ?? 0;
        // `required_persons` is the number of required person/worker hours per shift!
        hoursByShiftTag.set(item.shift_tag, hours + item.required_persons);
    }

    return hoursByShiftTag;
}

/**
 * Maps shift person line records (`IShiftPersonLineRec`) to the line payload for `storePeopleShiftAssignments`.
 */
export function mapShiftPersonLinesToStoreRecs(lines: IShiftPersonLineRec[]): IShiftPersonLineStoreRec[] {
    return lines.map(
        (line): IShiftPersonLineStoreRec => ({
            line_uuid: line.line_uuid,
            shifts: line.shifts.map(
                (shift): IShiftPersonLineStoreRecItem => ({
                    person_uuids: shift.persons.map(person => person.uuid),
                    shift_tag: shift.shift_tag,
                })
            ),
        })
    );
}
